import Container from 'components/Container/Container';
import Header from 'components/Header/Header';
import payrollProviderToLocalLogosMap from 'constants/payrollProviderToLocalLogosMap';
import ProviderLogo from 'components/Logo/ProviderLogo';
import {
  ImplementationDetail,
  ProviderConfig,
} from '@finch-api/common/dist/internal/connect/validate';
import { GoBackButton } from 'components/Button/GoBackButton';
import { ConnectError } from '../types';
import {
  Badge,
  Box,
  Center,
  HStack,
  Stack,
  StackSeparator,
} from '@chakra-ui/react';
import { useIsVisible } from 'hooks/use-is-visible';
import { useRef } from 'react';
import { DownArrowIcon } from 'components/Icons/DownArrowIcon';
import { AUTOMATED_AUTH_INSTRUCTIONS } from 'constants/automated-auth-instructions';
import { PayrollProviderId } from 'constants/types';
import { ImplementationKind } from '@finch-api/common/dist/internal/connect/authorize';
import { usePopOutInstructionsLink } from 'store/pop-out-instructions';
import { InstructionsPopout } from './InstructionsPopout';
import { ProviderAppShellError } from './ProviderAppShellError';
import { SCROLL_SHADOW } from './constants';
import BetaTag from 'components/VersionTag/BetaTag';
import { isImplementationInBeta } from 'utils/providers';
import { useFlag } from '@unleash/proxy-client-react';
import { FeatureFlag } from 'constants/feature-flags';
import { useAuthorizeContext } from '../AuthorizeContext';
import { AuthorizeLoadingPage } from '../LoadingPage/AuthorizeLoadingPage';
import { IncidentBanner } from 'components/IncidentBanner';
import {
  generateScopeForProvider,
  getInstructionConfig,
} from 'utils/scoped-instructions';

type AppShellProps = {
  children: React.ReactNode;
  provider: ProviderConfig;
  providerImplementation: ImplementationDetail;
  goBack?: () => void;
  headerText?: string;
  error: ConnectError | null;
  setError: (error: ConnectError | null) => void;
};

export const ProviderAppShell = ({
  children,
  provider,
  goBack,
  headerText,
  error,
  setError,
  providerImplementation,
}: AppShellProps) => {
  const pageBottom = useRef<HTMLDivElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const isVisible = useIsVisible(pageBottom, {
    root: root.current,
    // This is to ensure that the 'More fields' badge disappears when the button is visible on the page
    rootMargin: '30px',
  });

  const showInstructions = usePopOutInstructionsLink(
    (state) => state.popOutInstructionsLink,
  );

  const popOutInstructionsEnabled = useFlag(
    FeatureFlag.PopOutInstructionsEnabled,
  );

  const showIncidentsNotification = useFlag(
    FeatureFlag.ShowIncidentsNotification,
  );

  const { client, authorizeLoading, requestFinished, clientName, products } =
    useAuthorizeContext() || {};

  // Determine instructions link based on feature flag
  let instructionsLink: string | undefined;

  if (popOutInstructionsEnabled && products) {
    // Use scoped instructions when feature flag is enabled
    const implKind = providerImplementation.kind.replace(
      'finch_sandbox_',
      '',
    ) as ImplementationKind;
    const scope = generateScopeForProvider(
      provider.id as PayrollProviderId,
      implKind,
      products,
    );
    const config = getInstructionConfig(
      provider.id as PayrollProviderId,
      implKind,
      scope,
    );

    if (config) {
      instructionsLink = config.url;
    }
  }

  // Fallback to existing AUTOMATED_AUTH_INSTRUCTIONS if scoped instructions not available
  if (!instructionsLink) {
    instructionsLink =
      AUTOMATED_AUTH_INSTRUCTIONS[provider.id as PayrollProviderId]?.[
        providerImplementation.kind.replace(
          'finch_sandbox_',
          '',
        ) as ImplementationKind
      ];
  }

  return (
    <HStack gap="0" separator={<StackSeparator />} h="full">
      <Stack w="400px" h="full">
        <Container>
          {authorizeLoading && (
            <AuthorizeLoadingPage
              provider={provider.displayName}
              clientName={clientName || client?.name}
              requestFinished={requestFinished}
            />
          )}
          {isImplementationInBeta(providerImplementation) && <BetaTag />}
          <Center>
            <ProviderLogo
              src={payrollProviderToLocalLogosMap[provider.id].largeLogo}
              alt=""
            />
          </Center>

          {goBack && <GoBackButton onClick={goBack} />}

          <Stack
            overflow="auto"
            h="100%"
            w="100%"
            gap="0"
            scrollbar="hidden"
            {...SCROLL_SHADOW}
            ref={root}
          >
            <Stack flexGrow={1} gap="4">
              {error && (
                <ProviderAppShellError
                  error={error}
                  setError={setError}
                  provider={provider}
                  providerImplementation={providerImplementation}
                  instructionsLink={instructionsLink}
                />
              )}

              {showIncidentsNotification && (
                <IncidentBanner provider={provider} />
              )}

              {headerText && <Header>{headerText}</Header>}
              {children}
            </Stack>

            <Box ref={pageBottom} />
          </Stack>
          {isVisible === false && (
            <Badge
              variant="solid"
              colorScheme="black"
              pos="absolute"
              bottom="24px"
              fontSize="13px"
              rounded="full"
              px="10px"
              py="7.5px"
              gap="8px"
            >
              <DownArrowIcon />
              More fields
            </Badge>
          )}
        </Container>
      </Stack>
      {showInstructions && popOutInstructionsEnabled && <InstructionsPopout />}
    </HStack>
  );
};
